import type {
  CashHistoryFilters,
  HistoryGameType,
  HistorySortDirection,
  TournamentHistoryFilters,
} from '../../History/Type/HistoryTypes';
import type {
  CashFilter,
  CashFilterDraft,
  CashSortColumn,
  CurrencyTypeSelection,
  MttFilter,
  MttFilterDraft,
  MttSortColumn,
  SortState,
  TriStateSelection,
} from './HistoryConstants';

// ─── Input Normalizers ───────────────────────────────────────────────────────

const normalizeText = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const normalizeNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeInteger = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const normalizeTimestamp = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  const parsed = new Date(trimmed).getTime();
  return Number.isNaN(parsed) ? undefined : parsed;
};

const normalizeTriState = (value: TriStateSelection): boolean | undefined => {
  if (value === 'ANY') return undefined;
  return value === 'TRUE';
};

const normalizeCurrencyType = (value: CurrencyTypeSelection): CurrencyTypeSelection | undefined => {
  if (value === 'ANY') return undefined;
  return value;
};

// ─── Enum Mappers ────────────────────────────────────────────────────────────

const cashGameTypeMap: Record<CashFilter, HistoryGameType | undefined> = {
  all: undefined,
  texas: 'TEXAS',
  aof: 'AOF',
};

const tournamentStatusMap: Record<MttFilter, string[] | undefined> = {
  all: undefined,
  completed: ['COMPLETED'],
  cancelled: ['CANCELLED'],
  running: ['RUNNING'],
};

// ─── Filter Builders ─────────────────────────────────────────────────────────

export const buildCashFilters = (draft: CashFilterDraft): CashHistoryFilters => ({
  gameType: cashGameTypeMap[draft.gameType],
  roomNameContains: normalizeText(draft.roomNameContains),
  result: draft.result === 'ALL' ? undefined : draft.result,
  minNetAmount: normalizeNumber(draft.minNetAmount),
  maxNetAmount: normalizeNumber(draft.maxNetAmount),
  startTimeFrom: normalizeTimestamp(draft.startTimeFrom),
  startTimeTo: normalizeTimestamp(draft.startTimeTo),
  minSmallBlind: normalizeNumber(draft.minSmallBlind),
  maxSmallBlind: normalizeNumber(draft.maxSmallBlind),
  minBigBlind: normalizeNumber(draft.minBigBlind),
  maxBigBlind: normalizeNumber(draft.maxBigBlind),
  minAnte: normalizeNumber(draft.minAnte),
  maxAnte: normalizeNumber(draft.maxAnte),
  currency: draft.gameType === 'texas' ? 'USD' : normalizeText(draft.currency),
  currencyType:
    draft.gameType === 'texas'
      ? 'FIAT'
      : normalizeCurrencyType(draft.currencyType),
});

export const buildTournamentFilters = (draft: MttFilterDraft): TournamentHistoryFilters => ({
  statusIn: tournamentStatusMap[draft.status],
  nameContains: normalizeText(draft.nameContains),
  result: draft.result === 'ALL' ? undefined : draft.result,
  startTimeFrom: normalizeTimestamp(draft.startTimeFrom),
  startTimeTo: normalizeTimestamp(draft.startTimeTo),
  minBuyIn: normalizeNumber(draft.minBuyIn),
  maxBuyIn: normalizeNumber(draft.maxBuyIn),
  minEntryFee: normalizeNumber(draft.minEntryFee),
  maxEntryFee: normalizeNumber(draft.maxEntryFee),
  minGuaranteedPrizePool: normalizeNumber(draft.minGuaranteedPrizePool),
  maxGuaranteedPrizePool: normalizeNumber(draft.maxGuaranteedPrizePool),
  minLateRegistrationMinutes: normalizeInteger(draft.minLateRegistrationMinutes),
  maxLateRegistrationMinutes: normalizeInteger(draft.maxLateRegistrationMinutes),
  minRegisteredPlayers: normalizeInteger(draft.minRegisteredPlayers),
  maxRegisteredPlayers: normalizeInteger(draft.maxRegisteredPlayers),
  minMaxPlayers: normalizeInteger(draft.minMaxPlayers),
  maxMaxPlayers: normalizeInteger(draft.maxMaxPlayers),
  isSatellite: normalizeTriState(draft.isSatellite),
  gameType: draft.gameType === 'ANY' ? undefined : draft.gameType,
  rebuyEnabled: normalizeTriState(draft.rebuyEnabled),
  addonEnabled: normalizeTriState(draft.addonEnabled),
  currency: normalizeText(draft.currency),
  currencyType: normalizeCurrencyType(draft.currencyType),
});

// ─── Data Helpers ────────────────────────────────────────────────────────────

export const getUserStack = (
  entry: { playerBalances?: Array<{ userId: string; balance: number }> },
  userId: string
): number | undefined => {
  return entry.playerBalances?.find((player) => player.userId === userId)?.balance;
};

// ─── Sort Mappers ────────────────────────────────────────────────────────────

const cashSortFieldMap: Record<CashSortColumn, string> = {
  room: 'roomName',
  type: 'gameType',
  blinds: 'blindsBig',
  pot: 'pot',
  date: 'startedAt',
  pl: 'netAmount',
};

const mttSortFieldMap: Record<MttSortColumn, string> = {
  name: 'name',
  status: 'status',
  pos: 'position',
  buyin: 'buyIn',
  date: 'startTime',
  prize: 'prize',
};

export const getCashSortParams = (sort: SortState<CashSortColumn>): { sortBy: string; sortDirection: HistorySortDirection } => ({
  sortBy: cashSortFieldMap[sort.column],
  sortDirection: sort.direction === 'asc' ? 'ASC' : 'DESC',
});

export const getMttSortParams = (sort: SortState<MttSortColumn>): { sortBy: string; sortDirection: HistorySortDirection } => ({
  sortBy: mttSortFieldMap[sort.column],
  sortDirection: sort.direction === 'asc' ? 'ASC' : 'DESC',
});
